import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { readOriginal, useCostEstimation } from './useCostEstimation';
import { CostComparisonTable, CostTotal, IncompleteEstimateBanner } from './CostSummary';
import { EnvironmentCostCard } from './EnvironmentCostCard';

/**
 * Display-only scaffolder field showing what the submitted form will cost.
 *
 * One component serves both templates and tells them apart the only way that is
 * reliable: a Day-2 form carries an `_original` snapshot of the app as it
 * stands, and a Day-1 form does not. Full sees a total and a card per
 * environment; Day-2 sees Before / After / Different.
 *
 * It owns no form value. `onChange` is never called, so nothing here can reach
 * Terraform, the app snapshot or a pull request — and it never blocks
 * submission, however badly the pricing goes.
 */
export const CostEstimationField = (
  props: FieldExtensionComponentProps<unknown>,
) => {
  const formData =
    ((props as any).formContext?.formData as Record<string, unknown>) ?? {};
  const isDay2 = readOriginal(formData) !== undefined;
  const { data, loading, updating, error } = useCostEstimation(formData);

  if (loading) {
    return (
      <Box mt={2}>
        <LinearProgress />
        <div style={{ fontSize: 13, opacity: 0.75, marginTop: 8 }}>
          Estimating monthly cost…
        </div>
      </Box>
    );
  }

  if (!data) {
    // Nothing has ever succeeded. Say so plainly and get out of the way — the
    // form is still perfectly submittable.
    return (
      <Box mt={2} p={2} style={{ border: '1px solid #ffb74d', borderRadius: 4 }}>
        <div style={{ fontWeight: 600 }}>Estimate unavailable</div>
        <div style={{ fontSize: 13 }}>
          {error ?? 'Azure retail prices could not be retrieved.'} You can still
          continue.
        </div>
      </Box>
    );
  }

  // On Day-2 the cards follow the COMPARISON, not the After estimate. An
  // environment being removed has no After side at all, and dropping its card
  // would leave the biggest saving on the page with nothing to expand: the user
  // could see the total fall and not one line of what stopped being paid for.
  const environments = data.comparison
    ? data.comparison.environments
        .map(
          comparison =>
            data.after.environments.find(
              e => e.environment === comparison.environment,
            ) ??
            data.before?.environments.find(
              e => e.environment === comparison.environment,
            ),
        )
        .filter((e): e is NonNullable<typeof e> => e !== undefined)
    : data.after.environments;

  return (
    <Box mt={2}>
      {updating && (
        <Box mb={1}>
          <LinearProgress />
          <div style={{ fontSize: 12, opacity: 0.75 }}>Updating…</div>
        </Box>
      )}
      {error && !updating && (
        <Box mb={1} style={{ fontSize: 12, color: '#663c00' }}>
          Showing the last successful estimate — {error}
        </Box>
      )}

      {isDay2 && data.comparison ? (
        <>
          <CostComparisonTable comparison={data.comparison} />
          <Box mt={1} mb={2} style={{ fontSize: 12, opacity: 0.6 }}>
            Azure public retail prices in EUR, retrieved{' '}
            {new Date(data.generatedAt).toLocaleString()}. Excludes contract
            discounts, credits and tax.
          </Box>
        </>
      ) : (
        <CostTotal
          total={data.after.fixedMonthlyTotal}
          partial={data.status === 'partial'}
          generatedAt={data.generatedAt}
        />
      )}

      <IncompleteEstimateBanner unresolved={data.unresolved} />

      {environments.length === 0 ? (
        <Box style={{ fontSize: 13, opacity: 0.75 }}>
          No environment selected, so there is nothing to price yet.
        </Box>
      ) : (
        environments.map(environment => (
          <EnvironmentCostCard
            key={environment.environment}
            estimate={environment}
            comparison={data.comparison?.environments.find(
              e => e.environment === environment.environment,
            )}
            defaultExpanded={environments.length === 1}
          />
        ))
      )}
    </Box>
  );
};
